import React, {ReactElement, useCallback, useEffect, useMemo, useRef, useState,} from "react";
import {
    ActionButton,
    Callout,
    DefaultButton,
    GroupHeader,
    IColumn,
    IDetailsFooterProps,
    IDetailsHeaderProps,
    IDetailsList,
    IDetailsRowProps,
    IGroup,
    IRenderFunction,
    Label,
    Panel,
    PanelType,
    PrimaryButton,
    ProgressIndicator,
    Selection,
    SelectionMode,
    ShimmeredDetailsList,
    Spinner,
    Stack,
    Sticky,
    Text,
} from "@fluentui/react";
import {useTranslation} from "react-i18next";
import {SharedColors} from "@fluentui/theme";
import {ITenantColumn} from "./OnixElements";
import "../styles/OnixGroupedList.css";
import {OnixCallout} from "./OnixCallout";
import {hr} from "../locales/hr";
import {usePrevious} from "@fluentui/react-hooks";
import {useLocalStorage} from "../helpers/useLocalStorage";

interface IOnixGroup extends IGroup {
    fieldName: string;
}

function groupsGenerator<T>(
    itemsList: T[],
    fieldNames: Array<keyof T>,
    allowedGroupKeys:
        | Array<keyof T>
        | { [key in keyof Partial<T>]: (val: any, dto: T) => string },
    depth: number = 0,
    itemSubset: T[] = [],
): IOnixGroup[] {
    // Array of group objects
    const groupObjArr: IOnixGroup[] = [];

    const itemListToUse = depth > 0 ? itemSubset : itemsList;

    const allParentKeys = fieldNames.filter((value, index) => index <= depth);

    const reduceWithParentKeys = (item: T) =>
        allParentKeys
            .reduce<string[]>((previousValue, currentValue) => {
                if (allowedGroupKeys && (allowedGroupKeys as any)[currentValue]) {
                    const mapper = (allowedGroupKeys as any)[currentValue];
                    if (mapper) {
                        return previousValue.concat([mapper(item[currentValue], item)]);
                    }
                }
                return previousValue.concat([item[currentValue] as any]);
            }, [])
            .join(" | ");

    // Get the group names from the items list
    const groupNames = [
        ...(new Set(itemListToUse.map(reduceWithParentKeys)) as any),
    ];

    // Iterate through each group name to build proper group object
    groupNames.forEach((gn) => {
        // Count group items
        const filteredGroupItems = itemListToUse.filter(
            (item) => reduceWithParentKeys(item) === gn,
        );

        const groupLength = filteredGroupItems.length;

        // Find the first group index
        const groupIndex = itemsList.map(reduceWithParentKeys).indexOf(gn);

        const children = fieldNames[depth + 1]
            ? groupsGenerator(
                itemsList,
                fieldNames,
                allowedGroupKeys,
                depth + 1,
                filteredGroupItems,
            )
            : undefined;

        // Generate a group object
        groupObjArr.push({
            key: gn,
            name: gn,
            level: depth,
            count: groupLength,
            startIndex: groupIndex,
            children: children,
            data: filteredGroupItems,
            fieldName: fieldNames[depth] as string,
            // isCollapsed: true
        });
    });

    // The final groups array returned
    return groupObjArr;
}

interface IOnixGroupedList<T> {
    items?: T[] | null;
    allowedGroupKeys:
        | Array<keyof T>
        | {
        [key in keyof Partial<T>]: (
            val: Partial<T>[key],
            dto: Partial<T>,
        ) => string;
    };
    makeGroupHeaderAddonText?: (ts: T[]) => ReactElement | null;
    columns: ITenantColumn<T>[];
    onItemInvoked?: (item: T) => void;
    className?: string;
    onRenderRow?: IRenderFunction<IDetailsRowProps>;
    selectionPreservedOnEmptyClick?: boolean;
    selectionMode?: SelectionMode;
    itemSelected?: (item?: T) => void;
    itemsSelected?: (items: T[]) => void;
    onRenderDetailsHeader?: IRenderFunction<IDetailsHeaderProps> | undefined;
    onRenderDetailsFooter?: (
        ts: T[],
        groupKeys: string[],
        props: IDetailsFooterProps | undefined,
    ) => React.ReactElement | null;
    onColumnHeaderClick?: (ev?: any, column?: IColumn | undefined) => void;
    rightSideAddon?: (ts: T[], groupKeys: string[]) => JSX.Element;
    leftSideAddon?: (
        ts: T[],
        groupKeys: string[],
        orientation: "VERTICAL" | "HORIZONTAL",
        onDismiss?: () => void,
    ) => JSX.Element | null;
    sidebar?: JSX.Element;
    sidebarId?: string;
    children?: (clearSelection: () => void) => JSX.Element | null;
    tableIdentifier: string;
    style?: React.CSSProperties;
    keepFilterColumns?: boolean;
    defaultGroups?: Array<keyof T>;
    loading?: boolean;
    hideTopBar?: boolean;
    hideNoItemsFooter?: boolean;
    getId?: (data: any) => any;
    hideGrouping?: boolean;
    title?: keyof typeof hr | string;
    dontUseStickyTopBar?: boolean;
    listStyle?: React.CSSProperties;
    hideGroupTitle?: boolean;
    disabled?: boolean;
    clearSelectionRef?: React.MutableRefObject<() => void>;
}

export function OnixGroupedList<T>({
                                       items,
                                       allowedGroupKeys,
                                       onRenderDetailsHeader,
                                       onItemInvoked,
                                       itemSelected,
                                       selectionMode,
                                       selectionPreservedOnEmptyClick,
                                       onRenderRow,
                                       className,
                                       makeGroupHeaderAddonText,
                                       columns,
                                       onRenderDetailsFooter,
                                       onColumnHeaderClick,
                                       rightSideAddon,
                                       leftSideAddon,
                                       sidebar,
                                       sidebarId,
                                       children,
                                       tableIdentifier,
                                       style,
                                       keepFilterColumns,
                                       defaultGroups,
                                       loading,
                                       hideTopBar,
                                       hideNoItemsFooter,
                                       getId,
                                       hideGrouping,
                                       title,
                                       dontUseStickyTopBar,
                                       listStyle,
                                       hideGroupTitle,
                                       disabled: listIsDisabled,
                                       clearSelectionRef,
                                       itemsSelected,
                                   }: IOnixGroupedList<T>) {
    const [activeGroupKeys, setActiveGroupKeys] = useLocalStorage<Array<keyof T>>(
        "groupable-list-group-keys" +
        (allowedGroupKeys instanceof Array
                ? allowedGroupKeys
                : Object.keys(allowedGroupKeys)
        ).join(""),
        ((defaultGroups instanceof Array
            ? defaultGroups
            : defaultGroups != undefined
                ? Object.keys(defaultGroups)
                : undefined) ?? []) as any,
    );
    const [groups, setGroups] = useLocalStorage<IGroup[] | undefined>(
        "groupable-list-" +
        (allowedGroupKeys instanceof Array
                ? allowedGroupKeys
                : Object.keys(allowedGroupKeys)
        ).join(""),
        undefined,
    );
    const [sortedData, setSortedData] = useState<T[]>();
    const {t} = useTranslation();
    const [realColumns, setRealColumns] = useState<ITenantColumn<T>[]>();
    const [isShowingSidebar, setShowingSidebar] = useLocalStorage(
        "showing-sidebar-" + tableIdentifier,
        false,
    );
    const [sidebarDocked, setSidebarDocked] = useLocalStorage(
        "showing-sidebar-docked-" + tableIdentifier,
        false,
    );
    const shimmeredListRef = useRef<IDetailsList>(null);

    useEffect(() => {
        if (clearSelectionRef) {
            clearSelectionRef.current = clearSelection;
        }
    }, [clearSelectionRef]);

    useEffect(() => {
        if (items) {
            const localItems = [...items];
            const baseNullValue = "!";
            setSortedData(
                activeGroupKeys.length
                    ? localItems.sort((a, b) => {
                        const as = activeGroupKeys.reduce(
                            (previousValue, currentValue) => {
                                if (
                                    allowedGroupKeys &&
                                    (allowedGroupKeys as any)[currentValue]
                                ) {
                                    const mapper = (allowedGroupKeys as any)[currentValue];
                                    if (mapper)
                                        return previousValue + mapper(a[currentValue], a);
                                }
                                return (
                                    (previousValue ?? baseNullValue) +
                                    (a[currentValue] ?? baseNullValue)
                                );
                            },
                            "",
                        );
                        const bs = activeGroupKeys.reduce(
                            (previousValue, currentValue) => {
                                if (
                                    allowedGroupKeys &&
                                    (allowedGroupKeys as any)[currentValue]
                                ) {
                                    const mapper = (allowedGroupKeys as any)[currentValue];
                                    if (mapper)
                                        return previousValue + mapper(b[currentValue], b);
                                }
                                return (
                                    (previousValue ?? baseNullValue) +
                                    (b[currentValue] ?? baseNullValue)
                                );
                            },
                            "",
                        );

                        return as.localeCompare(bs);
                    })
                    : items,
            );
        }
    }, [items, activeGroupKeys]);

    useEffect(() => {
        if (sortedData) {
            setGroups(
                activeGroupKeys.length
                    ? groupsGenerator(sortedData, activeGroupKeys, allowedGroupKeys)
                    : undefined,
            );
        }
    }, [sortedData, activeGroupKeys]);

    useEffect(() => {
        if (keepFilterColumns) {
            setRealColumns(columns);
        } else {
            setRealColumns(
                columns.filter((f) => activeGroupKeys.indexOf(f.fieldName) == -1),
            );
        }
    }, [activeGroupKeys, keepFilterColumns, columns]);

    const onSelectionChanged = useCallback(() => {
        const selected: T[] = selection.getSelection() as any[];
        if (itemSelected) {
            let set: T | undefined;
            if (selected && selected.length) {
                set = selected[0];
            } else {
                set = undefined;
            }
            setImmediate(() => itemSelected(set));
        }
        if (itemsSelected) {
            setImmediate(() => itemsSelected(selected));
        }
    }, [itemSelected, itemsSelected]);

    const selection = useMemo(
        () =>
            new Selection({
                onSelectionChanged,
                getKey: (item: any) => (!!getId ? getId(item) : item?.id),
            }),
        [getId, onSelectionChanged],
    );
    const clearSelection = useCallback(() => {
        if (selection) {
            selection.setAllSelected(false);
        }
    }, [selection]);

    const [showContent, setShowContent] = useState(true);

    const previousIsShowingSidebar = usePrevious(isShowingSidebar);
    const previousSidebarDocked = usePrevious(sidebarDocked);

    useEffect(() => {
        if (
            previousIsShowingSidebar != undefined &&
            previousSidebarDocked != undefined
        ) {
            if (
                sidebarDocked &&
                isShowingSidebar &&
                (isShowingSidebar != previousIsShowingSidebar ||
                    sidebarDocked != previousSidebarDocked)
            ) {
                setImmediate(() => {
                    setShowContent(false);
                    setImmediate(() => {
                        setShowContent(true);
                    });
                });
            }
        }
    }, [
        sidebarDocked,
        isShowingSidebar,
        previousIsShowingSidebar,
        previousSidebarDocked,
    ]);

    const translatedGroupKeys = useMemo(
        () => activeGroupKeys.map(t) as any,
        [t, activeGroupKeys],
    );

    const [showItemMenuAtEvent, setShowItemMenuAtEvent] = useState<MouseEvent>();

    const showItemMenu = useCallback((item?: any, index?: number, ev?: Event) => {
        setShowItemMenuAtEvent(ev as MouseEvent);
        return false;
    }, []);

    const onRenderRowLocal = useCallback<IRenderFunction<IDetailsRowProps>>(
        (props, defaultRender) => {
            if (props) {
                if ((props.itemIndex ?? 0) % 2 === 0) {
                    if (!props.styles) {
                        props.styles = {};
                    }

                    props.styles = {
                        root: {
                            backgroundColor: "#f7f7f7",
                        },
                    };
                }
            }
            return onRenderRow
                ? onRenderRow(props, defaultRender)
                : defaultRender
                    ? defaultRender(props)
                    : null;
        },
        [onRenderRow],
    );

    const renderTopBar = useMemo(
        () => (
            <Stack
                horizontal
                horizontalAlign={"space-between"}
                verticalAlign={"center"}
                tokens={{childrenGap: 10}}
                styles={{root: {padding: "5px 0"}}}
            >
                <Stack
                    horizontal
                    verticalAlign={"center"}
                    className="left-side-addon-list-header"
                >
                    {title ? <Label style={{paddingLeft: 15}}>{t(title)}</Label> : null}
                    {leftSideAddon && sortedData
                        ? leftSideAddon(sortedData, translatedGroupKeys, "HORIZONTAL")
                        : null}
                </Stack>
                <Stack
                    horizontal
                    tokens={{childrenGap: 10, padding: "0 5px 0 0"}}
                    verticalAlign={"center"}
                >
                    {loading && <Spinner/>}
                    {hideGrouping != true &&
                    ((allowedGroupKeys instanceof Array && allowedGroupKeys?.length) ||
                        Object.keys(allowedGroupKeys).length) ? (
                        <OnixCallout
                            disabled={listIsDisabled}
                            trigger={(id, show) => (
                                <ActionButton
                                    disabled={listIsDisabled}
                                    iconProps={{
                                        iconName: "rowsGroup",
                                    }}
                                    text={t("grouping")}
                                    style={{marginRight: 10}}
                                    title={t("grouping")}
                                    id={id}
                                    onClick={(e) => show(e, undefined)}
                                />
                            )}
                            title={"grouping-settings"}
                        >
                            {(realign) => (
                                <Stack
                                    horizontal
                                    horizontalAlign={"space-between"}
                                    verticalAlign={"start"}
                                    tokens={{childrenGap: 10}}
                                >
                                    <Stack
                                        tokens={{childrenGap: 5}}
                                        horizontalAlign={"stretch"}
                                        verticalAlign={"start"}
                                    >
                                        <Text styles={{root: {minWidth: 200}}}>
                                            {t("active-groups")}
                                        </Text>
                                        {activeGroupKeys.map((value, index, array) => (
                                            <PrimaryButton
                                                disabled={listIsDisabled}
                                                key={value.toString() + "-" + index}
                                                styles={{
                                                    root: {
                                                        minWidth: 200,
                                                        marginLeft: index * 15,
                                                    },
                                                }}
                                                text={t(value.toString())}
                                                onClick={() =>
                                                    setActiveGroupKeys(
                                                        activeGroupKeys.filter((f) => f != value),
                                                    )
                                                }
                                            />
                                        ))}
                                        {!activeGroupKeys.length && (
                                            <Text
                                                variant={"small"}
                                                style={{
                                                    color: SharedColors.red10,
                                                }}
                                            >
                                                {t("no-items")}
                                            </Text>
                                        )}
                                    </Stack>
                                    <Stack
                                        tokens={{childrenGap: 5}}
                                        horizontalAlign={"stretch"}
                                        verticalAlign={"start"}
                                    >
                                        <Text styles={{root: {minWidth: 200}}}>
                                            {t("rest-of-the-groups")}
                                        </Text>
                                        {(allowedGroupKeys instanceof Array
                                                ? allowedGroupKeys
                                                : (Object.keys(allowedGroupKeys) as any)
                                        )
                                            .filter((f: any) => activeGroupKeys.indexOf(f) == -1)
                                            .map((m: any, i: number) => (
                                                <DefaultButton
                                                    disabled={listIsDisabled}
                                                    key={m.toString()}
                                                    styles={{root: {minWidth: 200}}}
                                                    text={t(m.toString())}
                                                    onClick={() =>
                                                        setActiveGroupKeys([...activeGroupKeys, m])
                                                    }
                                                />
                                            ))}
                                        {!(
                                            allowedGroupKeys instanceof Array
                                                ? allowedGroupKeys
                                                : (Object.keys(allowedGroupKeys) as any)
                                        ).filter((f: any) => activeGroupKeys.indexOf(f) == -1)
                                            .length && (
                                            <Text
                                                variant={"small"}
                                                style={{
                                                    color: SharedColors.red10,
                                                }}
                                            >
                                                {t("no-items")}
                                            </Text>
                                        )}
                                    </Stack>
                                </Stack>
                            )}
                        </OnixCallout>
                    ) : (
                        <div/>
                    )}
                    {rightSideAddon && sortedData
                        ? rightSideAddon(sortedData, translatedGroupKeys)
                        : null}
                    {sidebar ? (
                        <ActionButton
                            disabled={listIsDisabled}
                            text={t("search")}
                            iconProps={{iconName: "search"}}
                            onClick={() => setShowingSidebar(!isShowingSidebar)}
                        />
                    ) : null}
                </Stack>
            </Stack>
        ),
        [
            title,
            leftSideAddon,
            listIsDisabled,
            sortedData,
            translatedGroupKeys,
            loading,
            hideGrouping,
            allowedGroupKeys,
            activeGroupKeys,
            rightSideAddon,
            sidebar,
            isShowingSidebar,
        ],
    );

    return (
        <div className="onixGroupedList" style={style}>
            {!hideTopBar &&
                (!dontUseStickyTopBar ? (
                    <Sticky
                        isScrollSynced
                        stickyBackgroundColor={"white"}
                        stickyClassName={"sticky-onix-grouped-list-header"}
                    >
                        {renderTopBar}
                    </Sticky>
                ) : (
                    renderTopBar
                ))}
            {showContent ? (
                <Stack
                    horizontal
                    horizontalAlign={"stretch"}
                    style={sortedData && !sortedData?.length ? {paddingBottom: 15} : {}}
                >
                    <Stack horizontalAlign={"stretch"} style={{flex: 1}}>
                        <ShimmeredDetailsList
                            groups={groups}
                            styles={{
                                root: {margin: 0, padding: 0, ...(listStyle as any)},
                            }}
                            columns={realColumns}
                            enableShimmer={loading && (!sortedData || !sortedData.length)}
                            className={className}
                            onItemInvoked={listIsDisabled ? undefined : onItemInvoked}
                            onRenderRow={onRenderRowLocal}
                            compact
                            setKey={tableIdentifier}
                            componentRef={shimmeredListRef}
                            shimmerLines={1}
                            getKey={(item, index) => (!!getId ? getId(item) : item?.id)}
                            selectionPreservedOnEmptyClick={selectionPreservedOnEmptyClick}
                            selectionMode={
                                listIsDisabled ? SelectionMode.none : selectionMode
                            }
                            onItemContextMenu={listIsDisabled ? undefined : showItemMenu}
                            selection={selection}
                            onShouldVirtualize={() => false}
                            onRenderDetailsHeader={onRenderDetailsHeader}
                            onRenderDetailsFooter={(props) =>
                                sortedData && !sortedData.length ? (
                                    loading ? null : !hideNoItemsFooter ? (
                                        <Text
                                            variant={"small"}
                                            style={{
                                                color: SharedColors.red10,
                                                paddingLeft: 50,
                                            }}
                                        >
                                            {t("no-items")}
                                        </Text>
                                    ) : null
                                ) : onRenderDetailsFooter && sortedData ? (
                                    onRenderDetailsFooter(sortedData, translatedGroupKeys, props)
                                ) : null
                            }
                            onColumnHeaderClick={onColumnHeaderClick}
                            groupProps={{
                                onRenderHeader: (props, defaultRender) => {
                                    if (props) {
                                        const nameSegments = props.group?.name.split("|") ?? [];
                                        const realName =
                                            nameSegments[nameSegments.length - 1]?.trim() ?? " - - ";
                                        return (
                                            <GroupHeader
                                                {...props}
                                                onRenderTitle={() => (
                                                    <Stack
                                                        horizontal
                                                        verticalAlign="center"
                                                        tokens={{childrenGap: 10}}
                                                    >
                                                        <Label>
                                                            {t(realName?.trim() ?? " - - ")}
                                                            {!hideGroupTitle ? (
                                                                <Text style={{color: SharedColors.gray20}}>
                                                                    {" (" +
                                                                        t((props.group as any)?.fieldName ?? "") +
                                                                        ")"}
                                                                </Text>
                                                            ) : null}
                                                        </Label>
                                                        {makeGroupHeaderAddonText
                                                            ? makeGroupHeaderAddonText(props.group?.data)
                                                            : null}
                                                    </Stack>
                                                )}
                                            />
                                        );
                                    }

                                    return null;
                                },
                            }}
                            items={sortedData ?? []}
                        />
                        {loading && !!sortedData?.length && <ProgressIndicator/>}
                    </Stack>
                    {isShowingSidebar && sidebar && sidebarDocked ? (
                        <Stack>
                            <Stack
                                id={sidebarId}
                                tokens={{childrenGap: 10, maxWidth: 300, padding: 10}}
                                styles={{root: {minWidth: 300}}}
                                className="onix-grouped-list-sidebar"
                                verticalAlign={"start"}
                            >
                                {sidebar}
                                <Stack horizontal>
                                    <ActionButton
                                        disabled={listIsDisabled}
                                        text={
                                            sidebarDocked ? t("undock-sidebar") : t("dock-sidebar")
                                        }
                                        iconProps={{iconName: "DockLeftMirrored"}}
                                        onClick={() => setSidebarDocked(!sidebarDocked)}
                                    />
                                </Stack>
                            </Stack>
                        </Stack>
                    ) : null}
                </Stack>
            ) : null}
            {isShowingSidebar && sidebar && !sidebarDocked ? (
                <Panel
                    type={PanelType.smallFixedFar}
                    isBlocking={!sidebarDocked}
                    onDismiss={() => setShowingSidebar(!isShowingSidebar)}
                    isOpen
                    isLightDismiss
                    hidden={!isShowingSidebar}
                >
                    <Stack
                        id={sidebarId}
                        tokens={{childrenGap: 10}}
                        className="onix-grouped-list-sidebar"
                        verticalAlign={"start"}
                    >
                        {sidebar}
                        <Stack horizontal>
                            <ActionButton
                                disabled={listIsDisabled}
                                iconProps={{iconName: "DockLeftMirrored"}}
                                text={sidebarDocked ? t("undock-sidebar") : t("dock-sidebar")}
                                onClick={() => setSidebarDocked(!sidebarDocked)}
                            />
                        </Stack>
                    </Stack>
                </Panel>
            ) : null}
            <Callout
                hidden={!showItemMenuAtEvent}
                alignTargetEdge
                onDismiss={() => setShowItemMenuAtEvent(undefined)}
                target={showItemMenuAtEvent}
            >
                {leftSideAddon && sortedData && !!showItemMenuAtEvent
                    ? leftSideAddon(sortedData, translatedGroupKeys, "VERTICAL", () =>
                        setShowItemMenuAtEvent(undefined),
                    )
                    : null}
            </Callout>
            {children ? children(clearSelection) : null}
        </div>
    );
}
